import {
  Card,
  CardHeader,
  EmptyState,
  FormField,
  Input,
  Markdown,
  markdownSections,
  MenuList,
  MenuListItem,
  Page,
  PageHeader,
  SectionNav,
  Select,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
} from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { visibleSections } from '../admin/sections';
import { useDebounced } from '../lib/use-debounced';
import { useModules } from '../modules/context';
import { NotFoundPage } from '../pages/error-pages';
import { useSession } from '../session/session';
import {
  guideHref,
  guideLocale,
  guideQuery,
  searchGuides,
  searchWords,
  useGuideTexts,
  useVisibleGuides,
  type VisibleGuide,
} from './guides';

export interface HelpSearch {
  q?: string;
  area?: string;
}

/** Reads the search params of `/help`; anything unexpected is dropped. */
export function validateHelpSearch(input: Record<string, unknown>): HelpSearch {
  const text = (value: unknown) =>
    typeof value === 'string' && value.length > 0 && value.length <= 200 ? value : undefined;
  return { q: text(input.q), area: text(input.area) };
}

function byArea(guides: readonly VisibleGuide[]): VisibleGuide[][] {
  const areas = new Map<string, VisibleGuide[]>();
  for (const guide of guides) areas.set(guide.area, [...(areas.get(guide.area) ?? []), guide]);
  return [...areas.values()];
}

/** `/help`: the guides of the kernel and the modules, grouped by area and searchable. */
export function HelpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params: HelpSearch = useSearch({ strict: false });
  const [text, setText] = useState(params.q ?? '');
  const term = useDebounced(text.trim(), 250);
  const all = useVisibleGuides();
  const area = all.some((g) => g.area === params.area) ? params.area : undefined;
  const guides = useMemo(() => all.filter((g) => !area || g.area === area), [all, area]);
  const searching = searchWords(term).join('').length >= 2;
  const texts = useGuideTexts(guides, searching);
  const hits = searching ? searchGuides(texts, term) : [];

  useEffect(() => {
    if (term === (params.q ?? '')) return;
    void navigate({ to: '/help', search: { q: term || undefined, area }, replace: true });
  }, [term, area, params.q, navigate]);

  const areas = byArea(all);
  // The API reference is for those who set up and connect the app.
  const developers = visibleSections(useSession(), useModules()).length > 0 && !area;

  return (
    <>
      <PageHeader title={t('help.title')} description={t('help.description')} />
      <div className="flex flex-col gap-5">
        <div className="grid max-w-3xl gap-4 md:grid-cols-[1fr_16rem]">
          <FormField label={t('help.term')}>
            {(field) => (
              <Input
                {...field}
                type="search"
                autoComplete="off"
                value={text}
                onChange={(event) => setText(event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t('help.area')}>
            {(field) => (
              <Select
                id={field.id}
                value={area ?? 'all'}
                onValueChange={(value) =>
                  void navigate({
                    to: '/help',
                    search: { q: term || undefined, area: value === 'all' ? undefined : value },
                    replace: true,
                  })
                }
                options={[
                  { value: 'all', label: t('help.allAreas') },
                  ...areas.map((group) => ({
                    value: group[0]!.area,
                    label: t(group[0]!.areaLabel),
                  })),
                ]}
              />
            )}
          </FormField>
        </div>

        {searching ? (
          hits.length === 0 ? (
            <EmptyState
              icon="book-open"
              title={t('help.emptyTitle')}
              description={t('help.emptyDescription')}
            />
          ) : (
            <Card>
              <MenuList aria-label={t('help.results', { count: hits.length })}>
                {hits.map((hit) => (
                  <MenuListItem
                    key={guideHref(hit)}
                    icon={hit.guide.icon}
                    href={guideHref(hit)}
                    description={hit.snippet}
                    wrapDescription
                    trailing={
                      <span className="hidden text-xs md:inline">{t(hit.guide.areaLabel)}</span>
                    }
                  >
                    {hit.section ? `${hit.guide.title} › ${hit.section.title}` : hit.guide.title}
                  </MenuListItem>
                ))}
              </MenuList>
            </Card>
          )
        ) : guides.length === 0 ? (
          <EmptyState
            icon="book-open"
            title={t('help.noneTitle')}
            description={t('help.noneDescription')}
          />
        ) : (
          <div className="grid items-start gap-4 lg:grid-cols-2">
            {developers && (
              <Card>
                <CardHeader title={t('help.areas.development')} />
                <MenuList>
                  <MenuListItem
                    icon="plug"
                    href="/help/api"
                    description={t('help.api.description')}
                    wrapDescription
                  >
                    {t('help.api.title')}
                  </MenuListItem>
                </MenuList>
              </Card>
            )}
            {byArea(guides).map((group) => (
              <Card key={group[0]!.area} data-module-accent={group[0]!.accent}>
                <CardHeader title={t(group[0]!.areaLabel)} />
                <MenuList>
                  {group.map((g) => (
                    <MenuListItem
                      key={g.guide.id}
                      icon={g.icon}
                      href={`/help/${g.guide.id}`}
                      description={t(g.guide.summary)}
                      wrapDescription
                    >
                      {t(g.guide.title)}
                    </MenuListItem>
                  ))}
                </MenuList>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** `/help/<id>`: one guide with its table of contents. */
export function GuidePage() {
  const { guideId }: { guideId?: string } = useParams({ strict: false });
  const entry = useVisibleGuides().find((g) => g.guide.id === guideId);
  // A guide the person may not see does not exist for them.
  return entry ? <Guide key={entry.guide.id} entry={entry} /> : <NotFoundPage />;
}

function Guide({ entry }: { entry: VisibleGuide }) {
  const { t, i18n } = useTranslation();
  const hash = useLocation({ select: (location) => location.hash });
  const text = useQuery(guideQuery(entry.guide, guideLocale(i18n.language)));
  const sections = useMemo(
    () => (text.data ? markdownSections(text.data).filter((s) => s.depth === 2) : []),
    [text.data],
  );

  // Search hits lead to a section; it exists once the text is there.
  useEffect(() => {
    if (text.data && hash) document.getElementById(hash)?.scrollIntoView();
  }, [text.data, hash]);

  return (
    <Page data-module-accent={entry.accent}>
      <PageHeader
        title={t(entry.guide.title)}
        description={t(entry.guide.summary)}
        icon={entry.accent ? entry.icon : undefined}
        breadcrumbs={[
          { label: t('help.title'), href: '/help' },
          { label: t(entry.areaLabel), href: `/help?area=${encodeURIComponent(entry.area)}` },
          { label: t(entry.guide.title) },
        ]}
      />
      <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_14rem]">
        {text.isPending ? (
          <SkeletonRegion className="flex max-w-3xl flex-col gap-6">
            <SkeletonText lines={4} />
            <Skeleton className="h-5 w-48" />
            <SkeletonText lines={3} />
          </SkeletonRegion>
        ) : text.isError ? (
          <EmptyState icon="cloud-off" title={t('common.loadFailed')} />
        ) : (
          <Markdown source={text.data} />
        )}
        {sections.length > 1 && (
          <SectionNav
            label={t('help.onThisPage')}
            className="sticky top-[calc(var(--topbar-h)+var(--page-gutter))] hidden lg:flex"
            groups={[
              {
                label: t('help.onThisPage'),
                items: sections.map((s) => ({
                  href: `#${s.id}`,
                  label: s.title,
                  active: s.id === hash,
                })),
              },
            ]}
          />
        )}
      </div>
    </Page>
  );
}
